<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import CrossSectionTableRow from '@/components/calculation-result/CrossSectionTableRow.vue'
import { useCrossSectionStore } from '@/stores/crossSection'
import { useCrossSectionFilterStore } from '@/stores/crossSectionFilter'
import type { VDataTable } from 'vuetify/components'
import { computed, defineEmits, watch } from 'vue'

const crossSectionStore = useCrossSectionStore()
const crossSectionFilterStore = useCrossSectionFilterStore()

const {
  count,
  page,
  pageItems,
  fetchLoading: loading,
  availableInfo
} = storeToRefs(crossSectionStore)
const { baseCrossSectionFilterSet } = storeToRefs(crossSectionFilterStore)

const headers = computed<VDataTable['$props']['headers']>(() => {
  return [
    { title: 'Calculation type' },
    { title: 'Calculation method' },
    { title: 'Ship name' },
    {
      title: 'Normative flow velocity at bed',
      align: 'center',
      sortable: true,
      key: 'normative_flow_velocity_at_bed'
    },
    {
      title: 'Total area (Normative)',
      align: 'center',
      sortable: true,
      key: 'normative_total_area'
    },
    {
      title: 'Min required grading (Normative)',
      headerProps: { class: isShownHeader('normative_min_required_grading') }
    },
    {
      title: 'Effective safety factor (Normative)',
      align: 'center',
      headerProps: { class: isShownHeader('normative_effective_safety_factor') },
      sortable: true,
      key: 'normative_effective_safety_factor'
    },
    {
      title: 'Average rock layer thickness (Normative)',
      align: 'center',
      headerProps: { class: isShownHeader('normative_average_rock_layer_thickness') },
      sortable: true,
      key: 'normative_average_rock_layer_thickness'
    },
    {
      title: 'D blockmat (Normative)',
      align: 'center',
      headerProps: { class: isShownHeader('normative_d_blockmat') },
      sortable: true,
      key: 'normative_d_blockmat'
    },
    {
      title: 'D block (Normative)',
      align: 'center',
      headerProps: { class: isShownHeader('normative_d_block') },
      sortable: true,
      key: 'normative_d_block'
    },
    { title: 'Favorite', width: '4%', align: 'center' },
    { title: 'Export to excel', width: '5%', align: 'center' },
    { title: 'Export to MathCAD', width: '5%', align: 'center' },
    { title: '', width: '2%' }
  ]
})

const emit = defineEmits(['apply-filter', 'clear-all-filter'])

const isShownHeader = (key: string) => {
  return availableInfo.value.some((info) => key.includes(info)) ? '' : 'd-none'
}

watch(
  () => baseCrossSectionFilterSet.value.order_by,
  async () => {
    emit('apply-filter')
  }
)

const updateOptions = async (options: any) => {
  const sortBy = options.sortBy[0] ?? null
  if (sortBy) {
    baseCrossSectionFilterSet.value.order_by = (sortBy.order === 'desc' ? '' : '-') + sortBy.key
  } else {
    baseCrossSectionFilterSet.value.order_by = null
  }
  if (crossSectionStore.needFetchPagination(options.page)) {
    await crossSectionStore.fetchPaginationItems(baseCrossSectionFilterSet.value)
  }
  page.value = options.page
}
</script>

<template>
  <v-card border="opacity-35 sm" class="ma-2" variant="text">
    <v-data-table-server
      class="overflow-auto"
      :items="pageItems"
      :headers="headers"
      :loading="loading"
      :items-length="count"
      :itemsPerPageOptions="[{ title: '10', value: 10 }]"
      @update:options="updateOptions"
    >
      <template v-slot:item="{ item }">
        <CrossSectionTableRow :item="item"></CrossSectionTableRow>
      </template>
    </v-data-table-server>
  </v-card>
</template>
